import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Copy .env.local.example to .env.local and fill in your project credentials.'
  );
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: localStorage,
    autoRefreshToken: true,
  },
});

// ─── Typed helpers ────────────────────────────────────────────────────────────

/** Known category values — memories.categories is TEXT[] so any string is valid */
export type MemoryCategory =
  | 'Milestone'
  | 'Funny'
  | 'First'
  | 'Conversation'
  | 'Drawing'
  | 'Thankful'
  | 'Travel'
  | 'Achievement'
  | 'Contribution';

export type PromptType = 'milestone' | 'timehop' | 'bonding';

// ── Spaces ──────────────────────────────────────────────────────────────────

export type SpaceMemberRole     = 'owner' | 'member' | 'contributor' | 'child';
export type SpaceMemberStatus   = 'pending' | 'active' | 'revoked' | 'expired';
export type SpaceMemberLinkType = 'single_use' | 'time_limited';

export interface Space {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  deleted_at: string | null;
  avatar_url: string | null;
  ai_processing_enabled: boolean;
}

export interface SpaceMember {
  id: string;
  space_id: string;
  user_id: string | null;         // null = unregistered contributor
  role: SpaceMemberRole;
  child_ids: string[];            // empty = all children; populated = scoped
  display_name: string | null;
  status: SpaceMemberStatus;
  invite_token: string;
  invited_by: string | null;
  link_type: SpaceMemberLinkType; // single_use = one-time; time_limited = valid until expires_at
  expires_at: string | null;      // null for single_use; ISO timestamp for time_limited
  invitee_email: string | null;   // email the invite was sent to
  last_sent_at: string | null;    // last time an invite email was dispatched
  created_at: string;
}

// Legacy types kept for files not yet migrated — mark as deprecated.
// TODO: replace ConnectionRole with SpaceMemberRole throughout codebase
/** @deprecated — table dropped; use SpaceMember instead */
export type ConnectionRole = 'owner' | 'member' | 'contributor';
/** @deprecated — table dropped; use SpaceMemberStatus instead */
export type ConnectionStatus = 'pending' | 'accepted' | 'revoked' | 'expired';
/** @deprecated — table dropped; use SpaceMember instead */
export type ChildConnection = SpaceMember;

export interface Profile {
  id: string;
  nickname: string;
  avatar_url: string | null;
  current_streak: number;
  longest_streak: number;
  last_debrief_date: string | null;
  streak_shields: number;
  debrief_time: string;
  language: string;
  /** BCP-47 code of the language Mori speaks to the user in (transcription, story, chapter). Separate from `language` (UI). */
  content_language: string;
  theme: string | null;
  bonus_storage_mb: number | null;
  acorn_balance: number;
  mori_style: Record<string, unknown>;
  first_open_completed: boolean;
  /** NULL = use owned space (default); SET = user switched to another space */
  active_space_id: string | null;
  muted_card_types: string[];
  created_at: string;
}

export interface ChallengeCompletion {
  id: string;
  user_id: string;
  content_id: string;
  memory_id: string | null;
  completed_at: string;
  next_show_at: string | null;
  created_at: string;
  completion_type: 'memory' | 'acknowledgement';
  /** NULL = personal completion (parent/any cards); SET = space-scoped (child cards) */
  space_id: string | null;
  /** NULL = not child-specific; SET = child this completion counts for */
  child_id: string | null;
}

/**
 * Subjects are children, pets, and (later) toys — all stored in the same
 * `children` table behind the `subject_type` discriminator. Pets and toys
 * never get accounts, never appear in kid-login or child-connection flows,
 * and are skipped by birthday/promotion logic. The table name `children`
 * is a legacy artefact; consumers should reach for `Subject` when working
 * with the union and `Child` when the row must be a human child.
 */
export type SubjectType = 'child' | 'pet' | 'toy';

export interface Subject {
  id: string;
  space_id: string;
  name: string;
  /** Required for children, optional "Gotcha day" for pets, NULL for toys. */
  birthdate: string | null;
  avatar_url: string | null;
  avatar_color: string | null;
  gender: 'male' | 'female' | 'neutral' | null;
  promoted_to: string | null;     // child-only; always null for pet/toy
  /** Bridge to the active role='child' space_members row once this child
   *  has their own login ("Creator" account). NULL until promoted. Used
   *  by the `memories_child_select` RLS branch (20260424140000) to grant
   *  retroactive access to tagged family memories. Set by
   *  admin-create-child-account at promotion time. */
  promoted_to_space_member_id: string | null;
  username: string | null;        // child-only; always null for pet/toy
  subject_type: SubjectType;
  /** Free-text Mori context, e.g. "Golden Retriever, scared of fireworks". */
  ai_context: string | null;
  created_at: string;
  /** Derived by useChildren: true when this subject is a child whose
   *  `promoted_to_space_member_id` resolves to a space_members row with
   *  role='child' AND status='active'. Always false for pets/toys and for
   *  children who haven't been promoted (or whose promotion was revoked).
   *  Drives the "(when they have their own account)" suffix in the
   *  visibility readout and whether tagging them alone is enough to make
   *  the share-with-tagged toggle appear. */
  hasActiveChildAccount: boolean;
}

/** Subject narrowed to subject_type='child'. Use when the row must be human. */
export type Child = Subject & { subject_type: 'child' };

/** memories table — child_id and category columns removed; categories TEXT[] added */
export interface Memory {
  id: string;
  user_id: string;
  title: string | null;
  raw_transcript: string | null;
  ai_chapter: string | null;
  /** Short, first-person comment written by Mori alongside the chapter.
   *  Displayed as a distinct squirrel-toned note on the memory card. */
  mori_comment: string | null;
  categories: string[];
  image_url: string | null;   // storage path within memory-images bucket (private); use createSignedUrl to display
  audio_url: string | null;
  contributor_name: string | null;
  contributor_id: string | null;
  memory_type: 'voice' | 'written' | 'photo' | 'drawing' | 'measurement' | 'shortie';
  metadata: Record<string, unknown>;
  location?: string | null;
  /** Time-Capsule lifecycle — NOT an access/audience axis. See `audience` for who-can-see. */
  capsule_state?: 'default' | 'hidden' | 'time_capsule' | 'private';
  photo_count?: number;
  video_url?: string | null;
  thumbnail_url?: string | null;
  video_duration_seconds?: number | null;
  content_library_id?: string | null;
  space_id: string;
  /** 'approved' = visible in family feed (default); 'pending' = awaiting parent review; 'private' = child only */
  approval_status?: 'approved' | 'pending' | 'private';
  /** In-space reach: 'members' = Owner + Members only; 'space' = + scoped Contributors */
  audience?: 'members' | 'space';
  preserve_audio?: boolean;
  /** 'pending' = saved raw, not yet AI-processed; 'processed' = fully processed (default) */
  status?: 'pending' | 'processed';
  /** Gemini enrichment outcome. null = in-flight or timed-out; 'processed' = success; 'failed' = error; 'skipped' = AI deliberately off; 'transcribed' = transcript_only mode, full story not generated */
  ai_status?: 'processed' | 'failed' | 'skipped' | 'transcribed' | null;
  created_at: string;
  /** User-editable date the memory is *about* (distinct from immutable created_at). */
  memory_date: string;
}

/** @deprecated — table dropped; memory_children junction replaced by space_id on memories */
export interface MemoryChild {
  id: string;
  memory_id: string;
  child_id: string;
  created_at: string;
}

export interface MemoryParticipant {
  id: string;
  memory_id: string;
  // Exactly one of the three below is set per row:
  child_id: string | null;           // tagged child
  space_member_id: string | null;    // tagged space member (registered or unregistered)
  display_name: string | null;       // free-text (v2 feature)
  created_at: string;
}

export interface DailyDebrief {
  id: string;
  user_id: string;
  memory_id: string | null;
  debrief_date: string;
  created_at: string;
}

export interface Prompt {
  id: string;
  child_id: string;
  text: string;
  prompt_type: PromptType;
  scheduled_for: string;
  sent_at: string | null;
  created_at: string;
}

export interface AIPrompt {
  id: string;
  key: string;
  name: string;
  description?: string;
  system_prompt: string;
  model: string;
  temperature: number;
  active: boolean;
  updated_at: string;
  created_at: string;
}

export interface MemoryCategoryRow {
  id: string;
  key: string;
  label: string;
  emoji: string;
  color: string;
  active: boolean;
  sort_order: number;
  created_at: string;
}

export interface SparkQuestion {
  id: string;
  question: string;
  type: string;
  age_min_months: number;
  age_max_months: number;
  active: boolean;
  sort_order: number;
  created_at: string;
}


// ─── Subscriptions ────────────────────────────────────────────────────────────

/**
 * Tier slugs. `kindle` was hard-renamed to `seedling` in chat 1 of the tier
 * overhaul (migration 20260422140000). The DB row no longer exists and none
 * of the production write paths reference `kindle` — a couple of legacy
 * tombstone keys survive in client display maps so an in-flight upgrade
 * session with a stale tier_id still renders. Not a live tier.
 */
export type TierSlug = 'seedling' | 'ember' | 'keeper' | 'storyteller';

export interface SubscriptionTier {
  id: TierSlug;
  name: string;
  price_monthly: number | null;
  max_memories_per_month: number | null;
  max_children: number | null;
  max_contributors: number | null;
  audio_retention_days: number | null;
  share_link_hours: number | null;
  ai_processing: boolean;
  drawings_enabled: boolean;
  photos_enabled: boolean;
  collections_enabled: boolean;
  ads_enabled: boolean;
  storage_mb: number | null;
  max_photos_per_memory: number | null;
  max_drawings_per_month: number | null;
  max_time_capsules_per_year: number | null;
  web_storage_multiplier: number;
  hidden: boolean;
  stripe_price_id: string | null;
  // 20260422140100 — locked-matrix columns. NULL on any cap = unlimited.
  max_members: number | null;
  max_voice_duration_seconds: number | null;
  max_voice_per_week: number | null;
  max_video_duration_seconds: number | null;
  max_video_per_week: number | null;
  can_voice_context: boolean;
  can_map_pin: boolean;
  can_child_creator: boolean;
  can_custom_collections: boolean;
  can_active_capture: boolean;
  needs_ad_for_ai: boolean;
}

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'trialing'
  | 'paused';

export interface Subscription {
  id: string;
  user_id: string;
  tier_id: TierSlug;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: SubscriptionStatus;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  purchase_source: 'web' | 'ios' | 'android';
  last_changed_at: string | null;
  scheduled_tier_id: string | null;
  scheduled_at: string | null;
  created_at: string;
}

export interface SubscriptionChangeLog {
  id: string;
  user_id: string;
  from_tier_id: string | null;
  to_tier_id: string | null;
  change_type:
    | 'upgrade'
    | 'downgrade'
    | 'downgrade_applied'
    | 'cancelled_downgrade'
    | 'admin_grant'
    | 'admin_revoke'
    | 'grant_expired'
    | 'self_downgrade_from_past_due'
    | 'gift_redeemed';
  effective_at: string | null;
  scheduled_for: string | null;
  created_at: string;
}

export interface TrialGrant {
  id: string;
  user_id: string;
  tier_id: TierSlug;
  duration_days: number;
  source: string;
  granted_at: string;
  starts_at: string;
  ends_at: string;
  used: boolean;
  granted_by: string | null;
}

export interface UsageTracking {
  id: string;
  user_id: string;
  period_start: string;
  period_end: string;
  memories_recorded: number;
  storage_used_mb: number;
  created_at: string;
}

/**
 * One-time gift purchase (annual Keeper/Storyteller). No recurring
 * subscription — redemption creates a `trial_grants` row with
 * `source='gift'` and expires_at = redeem time + 1 year; the
 * `apply-scheduled-downgrades` cron auto-downgrades the recipient back
 * to seedling at expiry.
 */
export interface Gift {
  id: string;
  token: string;
  gifter_email: string;
  gifter_name: string;
  recipient_email: string;
  tier: 'keeper' | 'storyteller';
  occasion: 'new_baby' | 'birthday' | 'holiday' | 'general';
  personal_note: string | null;
  stripe_payment_intent_id: string;
  scheduled_send_at: string | null;
  sent_at: string | null;
  redeemed_at: string | null;
  redeemed_by: string | null;
  /** NULL for guest purchases (buyer not signed in at checkout time) */
  purchased_by: string | null;
  created_at: string;
}

/**
 * One of the 97 languages Mori can understand and speak in (Gemini Live API set).
 * Referenced by `profiles.content_language`. Rendered in the language picker.
 * tier: 1 = Safe (native-quality), 2 = Workable, 3 = Long-tail.
 */
export interface ContentLanguage {
  code: string;
  name_en: string;
  name_native: string;
  tier: 1 | 2 | 3;
  active: boolean;
  sort_order: number;
  created_at: string;
}
